import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { login, loginWithGoogle } from '../services/api';
import { authErrorToEnglish } from '../utils/authMessages';
import './Login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = ({ setIsAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [formError, setFormError] = useState('');
  const googleButtonRef = useRef(null);
  const navigate = useNavigate();

  const clearErrors = () => {
    setErrors({ email: '', password: '' });
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearErrors();
    const trimmedEmail = email.trim().toLowerCase();
    const newErrors = { email: '', password: '' };
    if (!trimmedEmail) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';
    if (newErrors.email || newErrors.password) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const data = await login(trimmedEmail, password);
      localStorage.setItem('token', data.token);
      setIsAuthenticated(true);
      toast.success('Successfully signed in!');
      navigate('/');
    } catch (error) {
      const message = authErrorToEnglish(error.response?.data?.error) || 'Sign in failed';
      setFormError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (response) => {
    if (!response?.credential) return;
    setGoogleLoading(true);
    try {
      const data = await loginWithGoogle(response.credential);
      localStorage.setItem('token', data.token);
      setIsAuthenticated(true);
      toast.success('Successfully signed in with Google!');
      navigate('/');
    } catch (error) {
      setFormError(authErrorToEnglish(error.response?.data?.error) || 'Sign in with Google failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleButtonRef.current) return;
    if (typeof window.google === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          auto_select: false
        });
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          text: 'continue_with',
          width: 320
        });
      };
      document.head.appendChild(script);
      return () => {};
    } else {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false
      });
      if (googleButtonRef.current && !googleButtonRef.current.hasChildNodes()) {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          text: 'continue_with',
          width: 320
        });
      }
    }
  }, [GOOGLE_CLIENT_ID]);

  return (
    <div className="login-page">
      <div className="login-page__bg">
        <div className="login-page__grid" aria-hidden="true" />
        <div className="login-page__gradient" aria-hidden="true" />
      </div>
      <div className="login-page__inner">
        <img src="/images/logo.png" alt="Wokkio" className="login-logo" />
        <div className="login-card">
          <div
            className="login-visual"
            aria-hidden="true"
            style={{ backgroundImage: 'url(/images/prometheus.png)' }}
          >
            <blockquote className="login-visual__quote">
              I hunted out and stored in fennel stalk the stolen source of fire
              that has proved a teacher to mortals in every art and a means to mighty ends.
              <cite>— Aeschylus, <em>Prometheus Bound</em></cite>
            </blockquote>
          </div>
          <div className="login-form-wrap">
            <form onSubmit={handleSubmit} className="login-form">
              {formError && (
                <div className="form-alert form-alert--error" role="alert">
                  {formError}
                </div>
              )}
              <div className={`form-group ${errors.email ? 'form-group--error' : ''}`}>
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrors((prev) => ({ ...prev, email: '' })); setFormError(''); }}
                  placeholder="Enter email"
                  required
                  autoComplete="email"
                  autoFocus
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'login-email-error' : undefined}
                />
                {errors.email && <span id="login-email-error" className="form-error">{errors.email}</span>}
              </div>
              <div className={`form-group ${errors.password ? 'form-group--error' : ''}`}>
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setErrors((prev) => ({ ...prev, password: '' })); setFormError(''); }}
                  placeholder="Enter password"
                  required
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'login-password-error' : undefined}
                />
                {errors.password && <span id="login-password-error" className="form-error">{errors.password}</span>}
              </div>
              <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              {GOOGLE_CLIENT_ID && (
                <div className="login-google-wrap">
                  <div className="login-google-divider">
                    <span>or</span>
                  </div>
                  <div ref={googleButtonRef} className="login-google-btn" />
                  {googleLoading && <p className="login-google-loading">Signing in with Google...</p>}
                </div>
              )}
              <div className="login-form-footer">
                <Link to="/register" className="login-form-footer__link">Create account</Link>
                <a href="/forgot-password" className="login-form-footer__link">Forgot password</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
